/**
 * components/tabs/UsersTab.jsx
 *
 * Admin-only user management panel.
 * Sub-tabs:
 *   Pending     — approve / reject new users
 *   All Users   — view, change role, block/unblock
 *   Permissions — per-role permission matrix (edit + save)
 *   Activity    — recent session + tab-visit log
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../context/UserContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function apiFetch(path, opts = {}) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts });
}

// ── Role list (for dropdowns) ────────────────────────────────────────────────
// F3: owner / user / viewer / pending. owner is bootstrap-only — not in dropdown.
const ROLES = ['user', 'viewer', 'pending'];

const ROLE_COLORS = {
  owner:   { color: '#a855f7', bg: 'rgba(168,85,247,.12)' },
  user:    { color: 'var(--amber-ink)', bg: 'rgba(218,119,32,.12)' },
  viewer:  { color: 'var(--ink-3)', bg: 'var(--surface-2)' },
  pending: { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  // legacy colours kept for any rows that haven't been migrated yet
  super_admin:  { color: '#a855f7', bg: 'rgba(168,85,247,.12)' },
  admin:        { color: 'var(--amber-ink)', bg: 'rgba(99,102,241,.12)' },
  power_trader: { color: '#10b981', bg: 'rgba(16,185,129,.12)' },
  trader:       { color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
      color: c.color, background: c.bg, borderRadius: 4, padding: '2px 7px',
      whiteSpace: 'nowrap',
    }}>
      {role?.replace('_', ' ')}
    </span>
  );
}

// ── Generic card wrapper ─────────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '.5px solid var(--hair)',
      borderRadius: 10, overflow: 'hidden', ...style,
    }}>
      {children}
    </div>
  );
}

// ── Btn helper ───────────────────────────────────────────────────────────────
function Btn({ onClick, variant = 'ghost', disabled, children, style = {} }) {
  const variants = {
    ghost:   { background: 'var(--surface-2)',     color: 'var(--ink-2)',  border: '.5px solid var(--hair)' },
    primary: { background: 'var(--amber-ink)',  color: '#fff',          border: 'none' },
    danger:  { background: 'var(--down-field-bg)',    color: 'var(--down-field-ink)',     border: '.5px solid var(--down-field-ink)' },
    success: { background: 'var(--up-field-bg)',    color: 'var(--up-field-ink)',     border: '.5px solid var(--up-field-ink)' },
  };
  const v = variants[variant] || variants.ghost;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 11, fontWeight: 600, borderRadius: 5,
        padding: '4px 10px', cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-ui)', opacity: disabled ? .5 : 1,
        transition: 'opacity .15s', ...v, ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
// PENDING SUB-TAB
// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
function PendingTab({ onAction }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/users/pending')
      .then(r => r.json())
      .then(d => setUsers(d.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (googleId, role = 'viewer') => {
    setBusy(b => ({ ...b, [googleId]: true }));
    await apiFetch(`/api/users/${googleId}/role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    setBusy(b => ({ ...b, [googleId]: false }));
    load();
    onAction?.();
  };

  const block = async (googleId) => {
    setBusy(b => ({ ...b, [googleId]: true }));
    await apiFetch(`/api/users/${googleId}/block`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ block: true }),
    });
    setBusy(b => ({ ...b, [googleId]: false }));
    load();
    onAction?.();
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>Loading…</div>;

  if (!users.length) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
        No pending approvals ✓
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px' }}>
      {users.map(u => (
        <Card key={u.google_id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
            {u.picture
              ? <img src={u.picture} alt={u.name} style={{ width: 38, height: 38, borderRadius: '50%' }}/>
              : <div style={{
                  width: 38, height: 38, borderRadius: '50%',
                  background: 'var(--surface-2)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 16, color: 'var(--ink-3)',
                }}>
                  {u.name?.[0]?.toUpperCase() || '?'}
                </div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{u.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{u.email}</div>
              {u.created_at && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  Requested {u.created_at.slice(0, 10)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {['viewer', 'trader', 'power_trader', 'admin'].map(role => (
                <Btn key={role}
                  variant="success"
                  disabled={busy[u.google_id]}
                  onClick={() => approve(u.google_id, role)}
                  style={{ fontSize: 11 }}
                >
                  ✓ {role.replace('_', ' ')}
                </Btn>
              ))}
              <Btn variant="danger" disabled={busy[u.google_id]} onClick={() => block(u.google_id)}>
                ✗ Deny
              </Btn>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
// ALL USERS SUB-TAB
// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
function AllUsersTab() {
  const { user: me } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState({});

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/users/')
      .then(r => r.json())
      .then(d => setUsers(d.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (googleId, role) => {
    setBusy(b => ({ ...b, [googleId]: true }));
    await apiFetch(`/api/users/${googleId}/role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    setBusy(b => ({ ...b, [googleId]: false }));
    load();
  };

  const toggleBlock = async (googleId, currentlyBlocked) => {
    setBusy(b => ({ ...b, [googleId]: true }));
    await apiFetch(`/api/users/${googleId}/block`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ block: !currentlyBlocked }),
    });
    setBusy(b => ({ ...b, [googleId]: false }));
    load();
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>Loading…</div>;

  const TH = ({ w, children, align = 'left' }) => (
    <th style={{
      width: w, padding: '9px 12px', textAlign: align,
      fontSize: 11, fontWeight: 700, letterSpacing: '.08em',
      textTransform: 'uppercase', color: 'var(--ink-3)',
      verticalAlign: 'middle', whiteSpace: 'nowrap',
    }}>{children}</th>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: 11, fontFamily: 'var(--font-ui)',
        tableLayout: 'fixed',
      }}>
        <colgroup>
          <col style={{ width: 200 }}/>  {/* User */}
          <col style={{ width: 'auto' }}/> {/* Email — takes remaining space */}
          <col style={{ width: 140 }}/>  {/* Role */}
          <col style={{ width: 110 }}/>  {/* Last Login */}
          <col style={{ width: 90 }}/>   {/* Status */}
          <col style={{ width: 90 }}/>   {/* Actions */}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '.5px solid var(--hair)' }}>
            <TH>User</TH>
            <TH>Email</TH>
            <TH>Role</TH>
            <TH>Last Login</TH>
            <TH align="center">Status</TH>
            <TH align="center">Actions</TH>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const isSelf  = u.google_id === me?.google_id;
            const blocked = !!u.is_blocked;
            const tdStyle = { padding: '9px 12px', verticalAlign: 'middle' };
            return (
              <tr key={u.google_id} style={{ borderBottom: '.5px solid var(--hair-strong)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                {/* Avatar + name */}
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                    {u.picture
                      ? <img src={u.picture} alt={u.name} style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0 }}/>
                      : <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--surface-2)', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                          {u.name?.[0]?.toUpperCase() || '?'}
                        </div>
                    }
                    <span style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.name}{isSelf && <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}>(you)</span>}
                    </span>
                  </div>
                </td>

                {/* Email */}
                <td style={{ ...tdStyle, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.email}
                </td>

                {/* Role */}
                <td style={tdStyle}>
                  {u.role === 'owner'
                    ? <RoleBadge role="owner" />
                    : (
                      <select
                        value={u.role}
                        disabled={isSelf || busy[u.google_id]}
                        onChange={e => changeRole(u.google_id, e.target.value)}
                        style={{
                          fontSize: 11, background: 'var(--surface-2)', color: 'var(--ink)',
                          border: '.5px solid var(--hair)', borderRadius: 4, padding: '3px 6px',
                          cursor: isSelf ? 'not-allowed' : 'pointer', width: '100%',
                        }}
                      >
                        {ROLES.map(r => (
                          <option key={r} value={r}>{r.replace('_', ' ')}</option>
                        ))}
                      </select>
                    )
                  }
                </td>

                {/* Last login */}
                <td style={{ ...tdStyle, color: 'var(--ink-3)', fontFamily: 'var(--font-data)', fontSize: 11 }}>
                  {u.last_login ? u.last_login.slice(0, 10) : '—'}
                </td>

                {/* Status */}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    color: blocked ? 'var(--down-field-ink)' : 'var(--up-field-ink)',
                    background: blocked ? 'var(--down-field-bg)' : 'var(--up-field-bg)',
                    borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap',
                  }}>
                    {blocked ? 'Blocked' : 'Active'}
                  </span>
                </td>

                {/* Actions */}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {!isSelf && (
                    <Btn
                      variant={blocked ? 'success' : 'danger'}
                      disabled={busy[u.google_id]}
                      onClick={() => toggleBlock(u.google_id, blocked)}
                    >
                      {blocked ? 'Unblock' : 'Block'}
                    </Btn>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
// PERMISSIONS SUB-TAB
// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──

// info-only rows — not toggleable, show fixed access policy
const INFO_ROWS = [
  {
    key: '__ai_feedback_btns',
    label: 'AI Feedback Buttons',
    fixedAccess: { owner: true, user: true, viewer: false },
  },
  {
    key: '__data_system_settings',
    label: 'Data & System Settings',
    fixedAccess: { owner: true, user: false, viewer: false },
  },
  {
    key: '__users_settings',
    label: 'Users Settings',
    fixedAccess: { owner: true, user: false, viewer: false },
  },
  {
    key: '__settings_tab',
    label: 'Settings Tab',
    fixedAccess: { owner: true, user: true, viewer: true },
  },
];

const PERM_KEYS = [
  // ── Top-level tabs ─────────────────────────────────────────────────────────
  { key: 'dashboard',                label: 'Dashboard' },
  { key: 'nifty',                    label: 'NIFTY' },
  // ── Market sub-tabs ────────────────────────────────────────────────────────
  { key: 'market_indices',           label: 'Market > Indices & Sectors' },
  { key: 'market_fo',                label: 'Market > F&O Stocks' },
  { key: 'market_rotation',          label: 'Market > Sector Rotation' },
  { key: 'market_rs',                label: 'Market > Relative Strength' },
  { key: 'market_movers',            label: 'Market > Market Movers' },
  { key: 'market_watchlists',        label: 'Market > Watchlists' },
  { key: 'market_events',            label: 'Market > Events Calendar' },
  { key: 'market_directory',         label: 'Market > NSE Directory' },
  { key: 'market_ai_tab',            label: 'Market > AI Feedback tab' },
  // ── Options sub-tabs ───────────────────────────────────────────────────────
  { key: 'options.option_chain',     label: 'Options > Chain' },
  { key: 'options.oi_analysis',      label: 'Options > OI Analysis' },
  { key: 'options.strategy_builder', label: 'Options > Strategy Builder' },
  // ── Other top-level tabs ───────────────────────────────────────────────────
  { key: 'scanner',                  label: 'Strategy Scanner' },
  { key: 'journal.trade_log',        label: 'Journal > Trade Log' },
  { key: 'journal.ai_analysis',      label: 'Journal > AI Analysis' },
  { key: 'ai_summary',               label: 'AI Summary' },
  { key: 'kite_connection',          label: 'Kite Connection' },
];

// F3: only viewer has configurable permissions; owner+user are always full-access
const CONFIGURABLE_ROLES = ['viewer'];
const LOCKED_ROLES = ['owner', 'user'];
const ALL_PERM_ROLES = [...LOCKED_ROLES, ...CONFIGURABLE_ROLES];

function PermissionsTab() {
  const [perms, setPerms]   = useState(null);
  const [dirty, setDirty]   = useState({});
  const [saving, setSaving] = useState(null);
  const [msg, setMsg]       = useState('');

  useEffect(() => {
    apiFetch('/api/users/permissions')
      .then(r => r.json())
      .then(d => { setPerms(d); setDirty({}); })
      .catch(() => {});
  }, []);

  const toggle = (role, key) => {
    setPerms(prev => ({
      ...prev,
      [role]: { ...prev[role], [key]: !prev[role]?.[key] },
    }));
    setDirty(d => ({ ...d, [role]: true }));
  };

  const save = async (role) => {
    setSaving(role);
    setMsg('');
    try {
      const r = await apiFetch(`/api/users/permissions/${role}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(perms[role]),   // flat {key: bool} map
      });
      if (r.ok) {
        setDirty(d => ({ ...d, [role]: false }));
        setMsg(`✓ ${role.replace('_', ' ')} permissions saved`);
      } else {
        setMsg('Save failed — try again');
      }
    } catch {
      setMsg('Network error');
    } finally {
      setSaving(null);
    }
  };

  if (!perms) return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>Loading…</div>;

  return (
    <div style={{ overflowX: 'auto', padding: '0 0 16px' }}>
      {msg && (
        <div style={{
          padding: '8px 16px', margin: '12px 16px', borderRadius: 6, fontSize: 11,
          color: msg.startsWith('✓') ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
          background: msg.startsWith('✓') ? 'var(--up-field-bg)' : 'var(--down-field-bg)',
        }}>
          {msg}
        </div>
      )}

      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: 11, fontFamily: 'var(--font-ui)',
        tableLayout: 'fixed',
      }}>
        <colgroup>
          <col style={{ width: '32%' }}/>
          {ALL_PERM_ROLES.map(r => <col key={r}/>)}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '.5px solid var(--hair)' }}>
            {/* Feature column header — verticalAlign bottom so it sits at same baseline as badges */}
            <th style={{
              padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
              letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-3)',
              verticalAlign: 'bottom',
            }}>
              Feature
            </th>
            {/* All role headers in a single loop — same structure, same height */}
            {ALL_PERM_ROLES.map(role => {
              const locked = LOCKED_ROLES.includes(role);
              const isDirty = !locked && dirty[role];
              return (
                <th key={role} style={{
                  padding: '8px 6px', textAlign: 'center',
                  verticalAlign: 'bottom', whiteSpace: 'nowrap',
                }}>
                  {/* Row 1: role badge — same vertical position for all 5 */}
                  <RoleBadge role={role}/>
                  {/* Row 2: subtitle row — always present to keep uniform height */}
                  <div style={{ height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                    {locked
                      ? <span style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', fontWeight: 400 }}>always on</span>
                      : isDirty
                        ? <Btn variant="primary" onClick={() => save(role)} disabled={saving === role}
                            style={{ fontSize: 11, padding: '1px 8px' }}>
                            {saving === role ? '…' : 'Save'}
                          </Btn>
                        : null
                    }
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {/* ── Editable permission rows ── */}
          {PERM_KEYS.map(({ key, label }) => (
            <tr key={key} style={{ borderBottom: '.5px solid var(--hair-strong)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ padding: '7px 16px', color: 'var(--ink-2)', fontSize: 11 }}>
                {label}
              </td>
              {ALL_PERM_ROLES.map(role => {
                const locked = LOCKED_ROLES.includes(role);
                const val = locked ? true : perms?.[role]?.[key];
                return (
                  <td key={role} style={{ padding: '7px 6px', textAlign: 'center' }}>
                    {locked
                      ? <div style={{
                          width: 20, height: 20, borderRadius: 4,
                          background: 'var(--amber-ink)', opacity: 0.45,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: '#fff',
                        }}>✓</div>
                      : <button
                          onClick={() => toggle(role, key)}
                          title={`${val ? 'Revoke' : 'Grant'} ${key} for ${role}`}
                          style={{
                            width: 20, height: 20, borderRadius: 4, cursor: 'pointer',
                            border: '.5px solid var(--hair)',
                            background: val ? 'var(--amber-ink)' : 'var(--surface-2)',
                            color: val ? '#fff' : 'var(--ink-3)',
                            fontSize: 11, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {val ? '✓' : ''}
                        </button>
                    }
                  </td>
                );
              })}
            </tr>
          ))}

          {/* ── Section divider ── */}
          <tr>
            <td colSpan={ALL_PERM_ROLES.length + 1} style={{
              padding: '6px 16px 2px',
              fontSize: 11, fontWeight: 700, letterSpacing: '.08em',
              textTransform: 'uppercase', color: 'var(--ink-3)',
              background: 'var(--surface-2)',
              borderTop: '.5px solid var(--hair)',
              borderBottom: '.5px solid var(--hair)',
            }}>
              Fixed Access (read-only — cannot be changed)
            </td>
          </tr>

          {/* ── Info-only rows ── */}
          {INFO_ROWS.map(({ key, label, fixedAccess }) => (
            <tr key={key} style={{ borderBottom: '.5px solid var(--hair-strong)', background: 'var(--surface-2)', opacity: 0.85 }}>
              <td style={{ padding: '7px 16px', color: 'var(--ink-3)', fontSize: 11, fontStyle: 'italic' }}>
                {label}
              </td>
              {ALL_PERM_ROLES.map(role => {
                const val = fixedAccess[role] ?? false;
                return (
                  <td key={role} style={{ padding: '7px 6px', textAlign: 'center' }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 4,
                      background: val ? 'var(--amber-ink)' : 'var(--surface-2)',
                      opacity: val ? 0.45 : 1,
                      border: val ? 'none' : '.5px solid var(--hair)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      color: val ? '#fff' : 'var(--ink-3)',
                    }}>
                      {val ? '✓' : ''}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--ink-3)' }}>
        To change a user's role, use the All Users tab. Owners have full access to all Settings panels; Users have access to Account settings only.
      </div>
    </div>
  );
}

// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
// ACTIVITY SUB-TAB — per-session summary view
// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──

function fmtDuration(ms) {
  if (ms < 60000) return '< 1 min';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtTime(ts) {
  if (!ts) return '—';
  // ts is "YYYY-MM-DD HH:MM:SS" UTC — display as-is (server logs in UTC, shown as IST elsewhere)
  return ts.slice(0, 16).replace('T', ' ');
}

function aggregateSessions(rows) {
  // Group by session_id (fall back to google_id + date if missing)
  const map = {};
  rows.forEach(row => {
    const sid = row.session_id || `${row.google_id}_${(row.created_at || '').slice(0, 10)}`;
    if (!map[sid]) {
      map[sid] = {
        sid,
        email: row.email || row.google_id,
        name: row.name,
        first: row.created_at || '',
        last: row.created_at || '',
        tabs: [],
      };
    }
    const s = map[sid];
    if (row.created_at && row.created_at < s.first) s.first = row.created_at;
    if (row.created_at && row.created_at > s.last)  s.last  = row.created_at;
    if (row.tab && !s.tabs.includes(row.tab)) s.tabs.push(row.tab);
  });
  return Object.values(map).sort((a, b) => b.first.localeCompare(a.first));
}

function ActivityTab() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Fetch last 7 days, up to 500 events so aggregation is meaningful
    apiFetch('/api/users/activity?limit=500&days=7')
      .then(r => r.json())
      .then(d => setData(d.activity || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>Loading…</div>;
  if (!data.length) return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
      No activity in the last 7 days
    </div>
  );

  const sessions = aggregateSessions(data);

  const TH = ({ w, children, align = 'left' }) => (
    <th style={{
      width: w, padding: '9px 12px', textAlign: align, fontSize: 11, fontWeight: 700,
      letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-3)',
      verticalAlign: 'middle', whiteSpace: 'nowrap',
    }}>{children}</th>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: 11, fontFamily: 'var(--font-ui)',
        tableLayout: 'fixed',
      }}>
        <colgroup>
          <col style={{ width: 210 }}/>  {/* Login Time */}
          <col style={{ width: 200 }}/>  {/* User */}
          <col style={{ width: 80 }}/>   {/* Duration */}
          <col style={{ width: 'auto' }}/> {/* Tabs Visited */}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '.5px solid var(--hair)' }}>
            <TH>Login Time (UTC)</TH>
            <TH>User</TH>
            <TH align="center">Duration</TH>
            <TH>Tabs Visited</TH>
          </tr>
        </thead>
        <tbody>
          {sessions.map(s => {
            const durationMs = s.first && s.last
              ? new Date(s.last.replace(' ', 'T') + 'Z') - new Date(s.first.replace(' ', 'T') + 'Z')
              : 0;
            const tdStyle = { padding: '8px 12px', verticalAlign: 'middle' };
            return (
              <tr key={s.sid} style={{ borderBottom: '.5px solid var(--hair-strong)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                {/* Login time */}
                <td style={{ ...tdStyle, fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {fmtTime(s.first)}
                </td>

                {/* User */}
                <td style={{ ...tdStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name || s.email}
                  </div>
                  {s.name && (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.email}
                    </div>
                  )}
                </td>

                {/* Duration */}
                <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-2)', fontFamily: 'var(--font-data)', fontSize: 11 }}>
                  {fmtDuration(durationMs)}
                </td>

                {/* Tabs visited */}
                <td style={tdStyle}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {s.tabs.length
                      ? s.tabs.map(t => (
                          <span key={t} style={{
                            fontSize: 11, fontWeight: 600, letterSpacing: '.05em',
                            textTransform: 'uppercase', padding: '2px 6px',
                            borderRadius: 4, background: 'var(--surface-2)',
                            color: 'var(--ink-3)', border: '.5px solid var(--hair)',
                            whiteSpace: 'nowrap',
                          }}>{t}</span>
                        ))
                      : <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
                    }
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ink-3)' }}>
        Showing last 7 days · {sessions.length} session{sessions.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──
// MAIN COMPONENT
// ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ── ──

const SUBTABS = [
  { id: 'users',       label: 'All Users' },
  { id: 'activity',    label: 'Activity' },
  { id: 'pending',     label: 'Pending' },
  { id: 'permissions', label: 'Permissions' },
];

export default function UsersTab({ initSub = '', onSubChange }) {
  const [sub, setSub] = useState(initSub || 'users');
  const [pendingCount, setPendingCount] = useState(null);

  // Load pending count for badge
  const refreshPendingCount = useCallback(() => {
    apiFetch('/api/users/pending')
      .then(r => r.json())
      .then(d => setPendingCount((d.users || []).length))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const setSubTab = (id) => {
    setSub(id);
    onSubChange?.(id);
  };

  return (
    <div>
      {/* Sub-tab nav */}
      <div style={{
        display: 'flex', borderBottom: '.5px solid var(--hair)',
        marginBottom: 0, gap: 0,
      }}>
        {SUBTABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={{
              padding: '7px 16px', fontSize: 11, fontWeight: sub === t.id ? 700 : 400,
              color: sub === t.id ? 'var(--ink)' : 'var(--ink-3)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: sub === t.id ? '2px solid var(--amber-ink)' : '2px solid transparent',
              fontFamily: 'var(--font-ui)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {t.label}
            {t.id === 'pending' && pendingCount > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700, background: '#ef4444', color: '#fff',
                borderRadius: 8, padding: '1px 5px', minWidth: 14, textAlign: 'center',
              }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <Card style={{ borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
        {sub === 'pending'     && <PendingTab     onAction={refreshPendingCount}/>}
        {sub === 'users'       && <AllUsersTab/>}
        {sub === 'permissions' && <PermissionsTab/>}
        {sub === 'activity'    && <ActivityTab/>}
      </Card>
    </div>
  );
}
